import { useEffect, useMemo, useRef, useState } from 'react';
import {
	transcodeVideo,
	getVideoByPathAndAudioVersion,
	uploadMutedVideoVersion
} from '../../../../api/video-library';

import {
	VIDEO_TRANSCODE_POLLING_RETRY_INTERVAL,
	VIDEO_TRANSCODING_STATUS,
	AZURE_VIDEO_UPLOADING_STATUS,
	AUDIO_VERSION
} from '../../../../core/constants';

//mui imports====
import useStyles from '../../styles';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/material';
import { Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import commonStyles from '../../../../commonStyles';
import DropDownSingle from '../../../../Components/DropDownSingle';
import HLSPlayer from '../../../../Components/HlsPlayer';

const VideoCard = ({ videoDetails, index, updateVideoDocOfAudioVersion }) => {
	const classes = useStyles();
	const [audioVersion, setAudioVersion] = useState(AUDIO_VERSION.UNMUTED);

	const [copiedText, setCopiedText] = useState('');
	const [uploadingMutedVideoState, setUploadingMutedVideoState] = useState(
		AZURE_VIDEO_UPLOADING_STATUS.UPLOAD
	);
	const [transcodingStatus, setTranscodingStatus] = useState({
		[AUDIO_VERSION.MUTED]:
			videoDetails[AUDIO_VERSION.MUTED]?.transcoded || VIDEO_TRANSCODING_STATUS.NOT_TRANSCODED,
		[AUDIO_VERSION.UNMUTED]:
			videoDetails[AUDIO_VERSION.UNMUTED]?.transcoded || VIDEO_TRANSCODING_STATUS.NOT_TRANSCODED
	});

	const currentVideoModeDetails = useMemo(
		() => videoDetails[audioVersion] || {},
		[videoDetails, audioVersion]
	);

	// Separate timeout refs for each version
	const transcodingTimeoutRefs = useRef({
		[AUDIO_VERSION.MUTED]: null,
		[AUDIO_VERSION.UNMUTED]: null
	});

	const commonStyleClasses = commonStyles();

	/**
	 * Checks the current transcoding status of a video.
	 * Displays success or error toast notifications based on the status.
	 * @param {string} transcodingAudioVersion - audio version
	 * @returns {Promise<boolean>} A promise that resolves to true if the video is transcoded or failed, false otherwise.
	 * @throws {Error} If there is an error fetching the video document.
	 */
	const checkVideoTranscodingStatus = async (transcodingAudioVersion) => {
		try {
			const encodedPath = encodeURIComponent(videoDetails[AUDIO_VERSION.UNMUTED]?.path);
			const { data } = await getVideoByPathAndAudioVersion(
				currentVideoModeDetails?.category,
				encodedPath,
				transcodingAudioVersion
			);
			const videoDoc = data?.data?.videoDoc;
			const currentTranscodingStatus = videoDoc?.transcoded;

			if (currentTranscodingStatus === VIDEO_TRANSCODING_STATUS.TRANSCODED) {
				toast.success(
					`Video transcoding completed successfully for the video title ${currentVideoModeDetails?.title} with audio - ${transcodingAudioVersion}`,
					{ position: toast.POSITION.BOTTOM_RIGHT }
				);
				updateVideoDocOfAudioVersion(index, transcodingAudioVersion, videoDoc);
				setTranscodingStatus((prev) => ({
					...prev,
					[transcodingAudioVersion]: currentTranscodingStatus
				}));
				return true;
			} else if (currentTranscodingStatus === VIDEO_TRANSCODING_STATUS.FAILED) {
				toast.error(
					`Video transcoding failed for the video title ${currentVideoModeDetails?.title} with audio - ${transcodingAudioVersion}`,
					{ position: toast.POSITION.BOTTOM_RIGHT }
				);
				updateVideoDocOfAudioVersion(index, transcodingAudioVersion, videoDoc);
				setTranscodingStatus((prev) => ({
					...prev,
					[transcodingAudioVersion]: currentTranscodingStatus
				}));
				return true;
			}

			return false;
		} catch (err) {
			console.error('Error fetching video doc:', err.message);
			toast.error('Error fetching video document.', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return true;
		}
	};

	/**
	 * Monitors the transcoding status of a video by periodically polling the server.
	 * @param {string} transcodingAudioVersion - audio version
	 * @returns {Promise<boolean>} A promise that resolves to true if polling should stop, false otherwise.
	 */
	const monitorVideoTranscodingStatus = async (transcodingAudioVersion) => {
		const isVideoTranscoded = await checkVideoTranscodingStatus(transcodingAudioVersion);
		if (!isVideoTranscoded) {
			transcodingTimeoutRefs.current[transcodingAudioVersion] = setTimeout(
				() => monitorVideoTranscodingStatus(transcodingAudioVersion),
				VIDEO_TRANSCODE_POLLING_RETRY_INTERVAL
			);
		}
	};

	/**
	 * Cleans up any active transcoding polling when stopping monitoring.
	 * @param {string} version - audio version
	 * @returns {void}
	 */
	const stopMonitoring = (version) => {
		if (transcodingTimeoutRefs.current[version]) {
			clearTimeout(transcodingTimeoutRefs.current[version]);
			transcodingTimeoutRefs.current[version] = null;
		}
	};

	/**
	 * Effect hook to monitor video transcoding status when the transcoding state is "PROCESSING".
	 * Stops the transcoding monitoring when the component unmounts to clean up resources.
	 * @returns {void}
	 */
	useEffect(() => {
		Object.entries(transcodingStatus).forEach(([version, status]) => {
			if (status === VIDEO_TRANSCODING_STATUS.PROCESSING) {
				monitorVideoTranscodingStatus(version);
			}
		});

		return () => {
			Object.keys(transcodingStatus).forEach((version) => stopMonitoring(version));
		};
	}, []);

	useEffect(() => {
		// Update transcoding status when video details change
		setTranscodingStatus((prev) => ({
			...prev,
			[AUDIO_VERSION.MUTED]:
				videoDetails[AUDIO_VERSION.MUTED]?.transcoded || VIDEO_TRANSCODING_STATUS.NOT_TRANSCODED,
			[AUDIO_VERSION.UNMUTED]:
				videoDetails[AUDIO_VERSION.UNMUTED]?.transcoded || VIDEO_TRANSCODING_STATUS.NOT_TRANSCODED
		}));
	}, [videoDetails]);

	const handleTranscodeVideo = async () => {
		try {

			toast.success('Video transcoding process successfully initiated!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			const transcodingAudioVersion = audioVersion;
			setTranscodingStatus((prev) => ({
				...prev,
				[transcodingAudioVersion]: VIDEO_TRANSCODING_STATUS.PROCESSING
			}));

			
			const payload = {
				fileName: currentVideoModeDetails.fileName,
				source: currentVideoModeDetails.azurePublicUrl,
				category: currentVideoModeDetails.category,
				subCategory: currentVideoModeDetails.subCategory,
				path: currentVideoModeDetails.path,
				audioVersion: transcodingAudioVersion
			};

			const { data } = await transcodeVideo(payload);
			const videoDocData = data?.data?.data;

			updateVideoDocOfAudioVersion(index, transcodingAudioVersion, videoDocData);
			monitorVideoTranscodingStatus(transcodingAudioVersion);
		} catch (err) {
			setTranscodingStatus((prev) => ({
				...prev,
				[audioVersion]: VIDEO_TRANSCODING_STATUS.FAILED
			}));

			updateVideoDocOfAudioVersion(index, audioVersion, videoDetails[audioVersion]);
			toast.error(err?.response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const handleUploadMutedVersion = async () => {
		try {
			setUploadingMutedVideoState(AZURE_VIDEO_UPLOADING_STATUS.UPLOADING);
			const { fileName, title, category, subCategory, description } =
				videoDetails[AUDIO_VERSION.UNMUTED];

			const res = await uploadMutedVideoVersion({
				fileName,
				title,
				category,
				subCategory,
				description
			});
			toast.success(res.data.data.message, { position: toast.POSITION.BOTTOM_RIGHT });
			setUploadingMutedVideoState(AZURE_VIDEO_UPLOADING_STATUS.UPLOADED);
		} catch (error) {
			setUploadingMutedVideoState(AZURE_VIDEO_UPLOADING_STATUS.UPLOAD);
			toast.error(error?.response?.data?.data?.message, { position: toast.POSITION.BOTTOM_RIGHT });
		}
	};

	const copyToClipboard = (text) => {
		try {
			navigator.clipboard.writeText(text).then(
				function () {
					toast.success('Text copied!', { position: toast.POSITION.BOTTOM_RIGHT });
				},
				function () {
					toast.error('Copy failed!', { position: toast.POSITION.BOTTOM_RIGHT });
					setCopiedText(text);
				}
			);
		} catch (err) {
			toast.error('Clipboard not supported in browser', { position: toast.POSITION.BOTTOM_RIGHT });
			setCopiedText(text);
		}
	};

	const doesCurrentVideoModeDetailsExist =
		currentVideoModeDetails && Object.keys(currentVideoModeDetails).length > 1;

	return (
		<>
			<Card style={{ width: '29rem', height: '45rem', margin: '2rem', borderRadius: '1rem' }}>
				{currentVideoModeDetails?.cdn77PathUrl ? (
					<HLSPlayer
						cdn77PathUrl={currentVideoModeDetails?.cdn77PathUrl}
						azurePublicUrl={currentVideoModeDetails?.azurePublicUrl}
					/>
				) : (
					// Added CardMedia as fallback method in case cdn77PathUrl doesn't exist
					<CardMedia
						component="video"
						sx={{ height: '15rem' }}
						controls
						src={currentVideoModeDetails?.azurePublicUrl}
					/>
				)}
				<CardContent>
					<Grid container spacing={1}>
						{doesCurrentVideoModeDetailsExist ? (
							<>
								<Grid item xs={12}>
									<Typography
										variant="h5"
										color="primary"
										fontWeight={600}
										className={commonStyleClasses.textOverflowStyles}
									>
										{currentVideoModeDetails?.title}
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography
										variant="h6"
										component="div"
										className={commonStyleClasses.textOverflowStyles}
									>
										{currentVideoModeDetails?.fileName}
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="text.secondary">
										{currentVideoModeDetails?.category}
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="text.secondary">
										{currentVideoModeDetails?.subCategory}
									</Typography>
								</Grid>

								<Grid item xs={6}>
									<Button
										disabled={true}
										className={classes.smallButton}
										size="medium"
										type="submit"
										variant="contained"
										onClick={() => copyToClipboard(currentVideoModeDetails?.azurePublicUrl)}
										endIcon={<ContentCopyIcon />}
									>
										Azure url
									</Button>
								</Grid>
								<Grid item xs={6}>
									<Button
										disabled={currentVideoModeDetails?.cdn77PathUrl === ''}
										className={classes.smallButton}
										size="medium"
										type="submit"
										variant="contained"
										onClick={() => copyToClipboard(currentVideoModeDetails?.cdn77PathUrl)}
										endIcon={<ContentCopyIcon />}
									>
										Cdn77 url
									</Button>
								</Grid>

								<Grid item xs={12}>
									{copiedText !== '' && (
										<TextField
											fullWidth
											className={classes.smallButton}
											size="medium"
											value={copiedText}
										/>
									)}
								</Grid>

								<Grid item xs={6} marginTop="1rem">
									<LoadingButton
										size="medium"
										type="submit"
										variant="contained"
										className={classes.smallButton}
										loading={
											transcodingStatus[audioVersion] === VIDEO_TRANSCODING_STATUS.PROCESSING
										}
										onClick={handleTranscodeVideo}
										disabled={
											transcodingStatus[audioVersion] === VIDEO_TRANSCODING_STATUS.TRANSCODED
										}
										color="success"
									>
										{transcodingStatus[audioVersion] === VIDEO_TRANSCODING_STATUS.NOT_TRANSCODED ||
										transcodingStatus[audioVersion] === VIDEO_TRANSCODING_STATUS.FAILED
											? 'Transcode'
											: transcodingStatus[audioVersion].toLowerCase()}
									</LoadingButton>
								</Grid>
							</>
						) : (
							<>
								<Grid item xs={12} style={{ margin: '2rem 0' }}>
									<Typography variant="body1" style={{ textAlign: 'center', fontWeight: 'bold' }}>
										Muted Audio Version Doesn't Exist for this video
									</Typography>
								</Grid>

								<Grid item xs={12} style={{ textAlign: 'center' }}>
									<LoadingButton
										size="medium"
										type="submit"
										variant="contained"
										className={classes.smallButton}
										loading={uploadingMutedVideoState === AZURE_VIDEO_UPLOADING_STATUS.UPLOADING}
										onClick={handleUploadMutedVersion}
										disabled={uploadingMutedVideoState === AZURE_VIDEO_UPLOADING_STATUS.UPLOADED}
									>
										{uploadingMutedVideoState} MUTED VERSION
									</LoadingButton>
								</Grid>
								{uploadingMutedVideoState === AZURE_VIDEO_UPLOADING_STATUS.UPLOADED && (
									<Grid item xs={12}>
										<Typography variant="body1" style={{ textAlign: 'center', fontWeight: 'bold' }}>
											Video Uploaded, Please Refresh To See Changes
										</Typography>
									</Grid>
								)}
							</>
						)}
						<Grid
							item
							xs={doesCurrentVideoModeDetailsExist ? 6 : 12}
							style={{ display: 'flex', justifyContent: 'flex-end' }}
						>
							<DropDownSingle
								isObjectEditor={false}
								directValue={audioVersion}
								updateDirectValue={(newValue) => setAudioVersion(newValue)}
								list={[AUDIO_VERSION.UNMUTED, AUDIO_VERSION.MUTED]}
								formControlStyles={{ width: '100%' }}
								containerStyles={{
									width: doesCurrentVideoModeDetailsExist ? '15rem' : '70%',
									margin: '0 auto'
								}}
								formControlSize={'small'}
							/>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
		</>
	);
};

export default VideoCard;
